/**
 * Nova instant-create machine (platform-neutral, no JSX): the composer's
 * "type a sentence, get a scheduled task" flow, shared by the web and native
 * composers.
 *
 * submit(sentence) = compile → auto-activate in ONE flow (the ChatGPT
 * "Scheduled tasks" behavior): on success the task simply appears on the board
 * with a toast. Only a failure surfaces UI: a typed {@link NovaCreateFailure}
 * the composer renders as an inline resolution callout. Error routing is ported
 * from {@link ./NovaBuilderSheet} handleBuild/handleActivate so entitlement,
 * active-limit, rate-limit and connector-required behave exactly like the
 * builder (same placements, same pending-draft resume tokens).
 *
 * Draft semantics (deliberate, per the redesign spec): a compiled draft whose
 * activation failed is KEPT server-side and shows on the board as a Draft row
 * with "Finish setup": nothing the user typed is ever lost. Only the explicit
 * Discard action deletes it.
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHaptics } from '../../hooks/useHaptics';
import { useLocale } from '../../hooks/useLocale';
import { useToast } from '../../hooks/useToast';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { createUpgradeContext } from '../../lib/monetization-context';
import { persistPendingNovaDraft } from '../../lib/pending-nova-draft';
import { novaCompileContext } from './nova-report-preview';
import type { AgentCapabilitiesPreference } from '../../lib/agent-capabilities-preference';
import {
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../../lib/product-analytics';
import {
  activateAutomation,
  compileAutomation,
  deleteAutomation,
  isNovaActiveLimit,
  isNovaConnectorRequired,
  isNovaEntitlementDenied,
  isNovaRateLimited,
  type NovaEntitlementDecision,
} from '../../lib/automation-api';

export type NovaCreateStatus = 'idle' | 'compiling' | 'activating';

export type NovaCreateFailure =
  /** Compile failed outright: nothing was created. */
  | { kind: 'compile'; message: string }
  /** Compile or activate hit a rate limit: retry later. */
  | { kind: 'rateLimit'; message: string; automationId?: string; name?: string }
  /** Active-automation cap (max > 0): pause one or discard the draft. */
  | { kind: 'limit'; message: string; automationId: string; name: string }
  /** The plan needs an app the user hasn't connected yet. */
  | {
      kind: 'connector';
      connectors: string[];
      automationId: string;
      name: string;
    }
  /** Generic activation failure: the draft is kept, retry or discard. */
  | { kind: 'activate'; message: string; automationId: string; name: string };

interface UseNovaCompilerOptions {
  /** The task is live: clear the input and refresh the board. */
  onCreated: () => void;
  /** Server state changed without a success (a draft now exists): refresh. */
  onDraftKept: () => void;
  /** Navigate to the connectors screen (platform-specific push). */
  onConnectApps: () => void;
}

function novaUpgradeContext(
  decision: NovaEntitlementDecision,
  entryPoint: string,
  draftId?: string,
  resumeToken?: string,
) {
  const isPreview =
    entryPoint.includes('compile') || entryPoint.includes('refine');
  return createUpgradeContext({
    decision,
    placement: isPreview ? 'nova_preview' : 'nova_activate',
    entryPoint,
    returnTo: draftId
      ? `/auto-apply?editAutomationId=${encodeURIComponent(draftId)}`
      : resumeToken
        ? `/auto-apply?resumeToken=${encodeURIComponent(resumeToken)}`
        : '/auto-apply',
    draftId,
    resumeToken,
  });
}

export function useNovaCompiler({
  onCreated,
  onDraftKept,
  onConnectApps,
}: UseNovaCompilerOptions) {
  const { t } = useTranslation('common');
  const { locale } = useLocale();
  const toast = useToast();
  const haptics = useHaptics();
  const upgradeModal = useUpgradeModal();

  const [status, setStatus] = useState<NovaCreateStatus>('idle');
  const [failure, setFailure] = useState<NovaCreateFailure | null>(null);
  // The most recent submit wins; a stale response must not clobber state.
  const submitRef = useRef(0);

  const activate = useCallback(
    async (automationId: string, name: string): Promise<boolean> => {
      try {
        await activateAutomation(automationId);
        trackProductEvent('nova_automation_activated', {
          creationFlow: 'composer',
        });
        haptics.trigger('success');
        toast.success(t('nova.builder.activated'));
        setFailure(null);
        onCreated();
        return true;
      } catch (err) {
        if (isNovaEntitlementDenied(err)) {
          trackProductEvent('nova_automation_activation_blocked', {
            reason: 'entitlement',
          });
          // Keep the inert draft so checkout can return to this exact plan.
          const upgrade = err.decision
            ? novaUpgradeContext(
                err.decision,
                'nova.composer.activate',
                automationId,
              )
            : ({
                placement: 'nova_activate',
                entryPoint: 'nova.composer.activate',
                planSet: 'individual',
                recommendedTier: 'pro',
                returnTo: `/auto-apply?editAutomationId=${encodeURIComponent(automationId)}`,
                draftId: automationId,
              } as const);
          setFailure(null);
          onDraftKept();
          upgradeModal.open(upgrade);
          return false;
        }
        if (isNovaActiveLimit(err)) {
          trackProductEvent('nova_automation_activation_blocked', {
            reason: 'active_limit',
            activeMax: err.meta?.max ?? null,
          });
          const max = err.meta?.max;
          // A plan that allows ZERO active automations is an upsell, not a
          // "pause one" situation: treat it like an entitlement denial.
          if (typeof max !== 'number' || max <= 0) {
            setFailure(null);
            onDraftKept();
            upgradeModal.open({
              placement: 'nova_activate',
              entryPoint: 'nova.composer.active_limit',
              planSet: 'individual',
              recommendedTier: 'pro',
              featureKey: 'individual.automations',
              reason: 'wrong_tier',
              returnTo: `/auto-apply?editAutomationId=${encodeURIComponent(automationId)}`,
              draftId: automationId,
            });
            return false;
          }
          setFailure({
            kind: 'limit',
            message: t('nova.limit.body', { max }),
            automationId,
            name,
          });
          onDraftKept();
          return false;
        }
        if (isNovaRateLimited(err)) {
          trackProductEvent('nova_automation_activation_blocked', {
            reason: 'rate_limit',
            scope: err.scope ?? 'automation',
          });
          const scope = err.scope ?? 'automation';
          setFailure({
            kind: 'rateLimit',
            message: t(`nova.rateLimit.${scope}.body`),
            automationId,
            name,
          });
          onDraftKept();
          return false;
        }
        if (isNovaConnectorRequired(err)) {
          trackProductEvent('nova_automation_activation_blocked', {
            reason: 'connector_required',
            connectorCount: err.connectors.length,
          });
          setFailure({
            kind: 'connector',
            connectors: err.connectors.length ? err.connectors : ['N/A'],
            automationId,
            name,
          });
          onDraftKept();
          return false;
        }
        setFailure({
          kind: 'activate',
          message:
            err instanceof Error
              ? err.message
              : t('nova.builder.activateFailed'),
          automationId,
          name,
        });
        trackProductEvent('nova_automation_activation_failed', {
          ...getErrorObservabilityProperties(err),
        });
        onDraftKept();
        return false;
      }
    },
    [t, toast, haptics, upgradeModal, onCreated, onDraftKept],
  );

  const submit = useCallback(
    async (sentence: string, capabilities?: AgentCapabilitiesPreference) => {
      const trimmed = sentence.trim();
      if (!trimmed || status !== 'idle') return;
      const submitId = ++submitRef.current;
      haptics.trigger('light');
      setFailure(null);
      setStatus('compiling');
      trackProductEvent('nova_compile_started', { locale });
      try {
        const compiled = await compileAutomation({
          sentence: trimmed,
          locale,
          // The task's own Research, Image and Result picks and the device
          // timezone (never the chat's sticky preference).
          ...novaCompileContext(capabilities),
        });
        if (submitId !== submitRef.current) return;
        trackProductEvent('nova_compile_succeeded', {
          stepCount: compiled.dsl?.steps.length ?? 0,
          triggerType: compiled.dsl?.trigger.type ?? 'unknown',
        });
        setStatus('activating');
        await activate(
          compiled.automationId,
          compiled.dsl?.name || trimmed,
        );
      } catch (err) {
        if (submitId !== submitRef.current) return;
        trackProductEvent('nova_compile_failed', {
          ...getErrorObservabilityProperties(err),
        });
        if (isNovaEntitlementDenied(err)) {
          // Stash the sentence so checkout can bring the user straight back
          // into this exact draft (?resumeToken= deep link).
          const resumeToken = await persistPendingNovaDraft({
            sentence: trimmed,
          }).catch(() => undefined);
          const upgrade = err.decision
            ? novaUpgradeContext(
                err.decision,
                'nova.composer.compile',
                undefined,
                resumeToken,
              )
            : ({
                placement: 'nova_preview',
                entryPoint: 'nova.composer.compile',
                planSet: 'individual',
                recommendedTier: 'pro',
                returnTo: resumeToken
                  ? `/auto-apply?resumeToken=${encodeURIComponent(resumeToken)}`
                  : '/auto-apply',
                resumeToken,
              } as const);
          upgradeModal.open(upgrade);
        } else if (isNovaRateLimited(err)) {
          setFailure({
            kind: 'rateLimit',
            message: t('nova.builder.rateLimited', {
              seconds: err.retryAfter ?? 60,
            }),
          });
        } else {
          setFailure({
            kind: 'compile',
            message:
              err instanceof Error
                ? err.message
                : t('nova.builder.compileFailed'),
          });
        }
      } finally {
        if (submitId === submitRef.current) setStatus('idle');
      }
    },
    [status, locale, t, haptics, upgradeModal, activate],
  );

  /** Retry activating the kept draft from the failure callout. */
  const retryActivate = useCallback(async () => {
    if (!failure || status !== 'idle') return;
    if (!('automationId' in failure) || !failure.automationId) return;
    const submitId = ++submitRef.current;
    setStatus('activating');
    try {
      await activate(failure.automationId, failure.name ?? '');
    } finally {
      if (submitId === submitRef.current) setStatus('idle');
    }
  }, [failure, status, activate]);

  /** Explicitly throw the kept draft away (best-effort delete). */
  const discardDraft = useCallback(() => {
    if (!failure) return;
    if ('automationId' in failure && failure.automationId) {
      void deleteAutomation(failure.automationId).catch(() => {});
    }
    trackProductEvent('nova_draft_discarded', { failureKind: failure.kind });
    setFailure(null);
    onDraftKept();
  }, [failure, onDraftKept]);

  /** Close the callout, keeping the draft on the board ("Finish setup"). */
  const dismiss = useCallback(() => {
    setFailure(null);
  }, []);

  /** Leave for the connectors screen, keeping the draft to activate later. */
  const connectApps = useCallback(() => {
    setFailure(null);
    onConnectApps();
  }, [onConnectApps]);

  return {
    status,
    failure,
    submit,
    retryActivate,
    discardDraft,
    dismiss,
    connectApps,
  };
}
